"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IconCheck, IconLoader2, IconPlus } from "@tabler/icons-react"

import { createHotel, getHotels } from "@/lib/hotels-api"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/stores/auth-store"
import type { Hotel } from "@/types"

type Row =
  | { kind: "hotel"; hotel: Hotel }
  | { kind: "add"; name: string }

/**
 * Hotel picker for a trip's accommodation block. Follows CatalogCombobox's
 * interaction contract (search-as-you-type, arrow/enter navigation, options
 * preventDefault on mousedown so the click beats the blur) but does NOT reuse
 * it: that component fetches treatment categories itself and is typed to
 * TreatmentCatalogItem.
 *
 * Free text is never blocked. Typing sets hotel_name and clears hotel_id, so a
 * hotel nobody has registered still saves exactly as it did before the registry
 * existed. Picking a row links it; the trailing "add" row registers what was
 * typed and links that. The backend returns the existing row when the name is
 * already known, so adding twice cannot duplicate.
 *
 * Pick-only (hotel_create_permission_enabled, ArtDent 261020): hotels are added
 * in Settings › Hotels, so there is no "add" row, and text that is not a hotel
 * from the list goes back to what the field held on focus. Clearing the field
 * still clears it, and a legacy free-text name nobody touches stays as it is.
 */
export function HotelCombobox({
  hotelId,
  hotelName,
  onChange,
  placeholder,
  addLabel,
  emptyLabel,
  pickOnlyEmptyLabel,
  onAddFailed,
  disabled,
}: {
  hotelId: string | null
  hotelName: string
  onChange: (next: { hotelId: string | null; hotelName: string }) => void
  placeholder: string
  /** Copy for the trailing register-this-one row, e.g. `Add "Grand Palace"`. */
  addLabel: (name: string) => string
  /** Shown when the registry is empty and nothing has been typed. */
  emptyLabel: string
  /** Pick-only: shown when nothing in the list matches, pointing at Settings › Hotels. */
  pickOnlyEmptyLabel: string
  onAddFailed?: () => void
  disabled?: boolean
}) {
  const pickOnly = useAuthStore(
    (state) => state.user?.feature_flags?.hotel_create_permission_enabled ?? false
  )
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [adding, setAdding] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Pick-only: what the field held when it took focus — or the row just
  // picked — which a blur with unlisted text restores.
  const committedRef = useRef({ hotelId, hotelName })

  useEffect(() => {
    let cancelled = false
    getHotels({ activeOnly: true })
      .then((list) => {
        if (!cancelled) setHotels(list)
      })
      // A registry we cannot read must not break the trip form — the field
      // degrades to the plain free-text input it replaced.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const query = hotelName.trim()

  const rows = useMemo<Row[]>(() => {
    const q = query.toLowerCase()
    // A linked hotel means the input holds its exact name; show the whole list
    // rather than filtering down to the one row already chosen.
    const matches = hotelId
      ? hotels
      : hotels.filter((h) => !q || h.name.toLowerCase().includes(q) || (h.city ?? "").toLowerCase().includes(q))

    const out: Row[] = matches.map((hotel) => ({ kind: "hotel", hotel }))

    const exact = hotels.some((h) => h.name.toLowerCase() === q)
    if (q && !exact && !pickOnly) out.push({ kind: "add", name: query })

    return out
  }, [hotels, hotelId, query, pickOnly])

  const choose = useCallback(
    async (row: Row | undefined) => {
      if (!row) return

      if (row.kind === "hotel") {
        committedRef.current = { hotelId: row.hotel.id, hotelName: row.hotel.name }
        onChange({ hotelId: row.hotel.id, hotelName: row.hotel.name })
        setOpen(false)
        inputRef.current?.blur()
        return
      }

      setAdding(true)
      try {
        const hotel = await createHotel({ name: row.name })
        setHotels((prev) => (prev.some((h) => h.id === hotel.id) ? prev : [...prev, hotel]))
        onChange({ hotelId: hotel.id, hotelName: hotel.name })
        setOpen(false)
        inputRef.current?.blur()
      } catch {
        // Leave the typed text in place — it still saves as free text.
        onAddFailed?.()
      } finally {
        setAdding(false)
      }
    },
    [onChange, onAddFailed]
  )

  return (
    <div className="relative">
      <div
        className={cn(
          "flex h-[34px] items-center gap-2 rounded-md border border-input bg-transparent px-3",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15",
          disabled && "opacity-50"
        )}
      >
        {hotelId && <IconCheck className="size-3.5 flex-none text-primary" aria-hidden />}
        <input
          ref={inputRef}
          value={hotelName}
          disabled={disabled}
          onChange={(e) => {
            // Typing breaks the link: the text is free text again until a row
            // is picked or added.
            onChange({ hotelId: null, hotelName: e.target.value })
            setActive(0)
            setOpen(true)
          }}
          onFocus={() => {
            committedRef.current = { hotelId, hotelName }
            setOpen(true)
          }}
          onBlur={() => {
            setOpen(false)
            // A pick lands here before the parent re-renders, with this
            // render's stale props — committedRef already holds the pick, so
            // restoring it is the same pick again.
            const committed = committedRef.current
            if (pickOnly && !hotelId && hotelName.trim() !== "" && hotelName !== committed.hotelName) {
              // A listed hotel's exact name, typed rather than clicked, links it.
              const typed = hotels.find((h) => h.name.toLowerCase() === query.toLowerCase())
              onChange(typed ? { hotelId: typed.id, hotelName: typed.name } : committed)
            }
          }}
          onKeyDown={(e) => {
            if (!open) return
            if (e.key === "ArrowDown") {
              setActive((a) => Math.min(a + 1, rows.length - 1))
              e.preventDefault()
            } else if (e.key === "ArrowUp") {
              setActive((a) => Math.max(a - 1, 0))
              e.preventDefault()
            } else if (e.key === "Enter" && rows.length > 0) {
              void choose(rows[active])
              e.preventDefault()
            } else if (e.key === "Escape") {
              inputRef.current?.blur()
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none placeholder:text-text-dim/60"
        />
        {adding && <IconLoader2 className="size-3.5 flex-none animate-spin text-text-dim" aria-hidden />}
      </div>

      {open && (
        <div className="absolute inset-x-0 top-[calc(100%+4px)] z-30 max-h-60 overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
          {rows.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-text-dim">{pickOnly ? pickOnlyEmptyLabel : emptyLabel}</div>
          ) : (
            rows.map((row, index) => (
              <button
                key={row.kind === "hotel" ? row.hotel.id : "__add"}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  void choose(row)
                }}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left",
                  index === active && "bg-muted"
                )}
              >
                {row.kind === "add" ? (
                  <>
                    <IconPlus className="size-3.5 flex-none text-primary" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-[13px]">{addLabel(row.name)}</span>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{row.hotel.name}</span>
                    {row.hotel.city && (
                      <span className="flex-none text-[11px] text-text-dim">{row.hotel.city}</span>
                    )}
                  </>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
